import React, { Component, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('🚨 Component Error Boundary Caught:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // For CYA Routes: Save emergency backup of editor state
    this.saveEmergencyBackup();
  }

  saveEmergencyBackup = () => {
    try {
      // Try to save current editor state to localStorage
      const currentUrl = window.location.pathname;
      if (currentUrl.includes('/editor')) {
        const emergencyData = {
          timestamp: Date.now(),
          url: currentUrl,
          error: this.state.error?.message || 'Unknown error',
          // Could grab route data from store here if needed
        };
        localStorage.setItem('cyaRoutes_emergencyBackup', JSON.stringify(emergencyData));
        console.log('💾 Emergency backup saved to localStorage');
      }
    } catch (backupError) {
      console.error('Failed to save emergency backup:', backupError);
    }
  };

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-[200px] flex items-center justify-center p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center space-x-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <h3 className="text-lg font-semibold text-red-800">
                Something went wrong
              </h3>
            </div>
            
            <p className="text-red-700 mb-4">
              Don't worry - your work has been saved automatically. 
              You can try refreshing this section or return to the homepage.
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mb-4">
                <summary className="text-sm text-red-600 cursor-pointer">
                  Error Details (Development)
                </summary>
                <pre className="text-xs bg-red-100 p-2 rounded mt-2 overflow-auto">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div className="flex space-x-3">
              <button
                onClick={this.handleRetry}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Try Again</span>
              </button>
              
              <button
                onClick={this.handleGoHome}
                className="flex items-center space-x-2 px-4 py-2 border border-red-300 text-red-700 rounded hover:bg-red-50 transition-colors"
              >
                <Home className="w-4 h-4" />
                <span>Go Home</span>
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Convenience wrapper for specific use cases
export const RouteEditorErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ErrorBoundary
    onError={(error, errorInfo) => {
      console.error('🎨 Route Editor Error:', error.message);
      // Could trigger autosave here or show specific recovery options
    }}
    fallback={
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 m-4">
        <div className="flex items-center space-x-2 mb-2">
          <AlertTriangle className="w-5 h-5 text-yellow-600" />
          <h4 className="font-medium text-yellow-800">Editor Component Error</h4>
        </div>
        <p className="text-yellow-700 text-sm mb-3">
          The route editor encountered an issue. Your work is automatically saved.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-3 py-1 bg-yellow-600 text-white rounded text-sm hover:bg-yellow-700"
        >
          Reload Editor
        </button>
      </div>
    }
  >
    {children}
  </ErrorBoundary>
);

export const PhotoGalleryErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ErrorBoundary
    fallback={
      <div className="bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
        <AlertTriangle className="w-8 h-8 text-gray-500 mx-auto mb-2" />
        <p className="text-gray-600 text-sm">
          Photo gallery temporarily unavailable
        </p>
      </div>
    }
  >
    {children}
  </ErrorBoundary>
);

export const MapErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ErrorBoundary
    fallback={
      <div className="bg-gray-200 rounded-lg p-8 text-center min-h-[400px] flex items-center justify-center">
        <div>
          <AlertTriangle className="w-12 h-12 text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">Map Unavailable</h3>
          <p className="text-gray-600 text-sm">
            The map component encountered an error. Please refresh the page.
          </p>
        </div>
      </div>
    }
  >
    {children}
  </ErrorBoundary>
);